import {
  Box,
  Button,
  Container,
  Divider,
  Paper,
  Typography,
} from "@mui/material";
import {
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import axios from "axios";
import { useEffect, useState } from "react";
import capture, { setPersonProperties } from "../../capture";
import { hostname, parseError } from "../../utils";
import { trackPurchaseConversion } from "../../utils/gtagUtils";
import {
  SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION,
  SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY,
} from "../../constants";

interface SelectedServicePackage {
  id: string;
  name: string;
  price: number;
  billingType?: string;
  billingInterval?: string;
}

const PaymentStep = ({
  displayName,
  email,
  setModalOpen,
  getUsage,
  getToken,
  eventListener,
  productPlan,
  productPlanName,
  hasConfiguration,
  billingPeriod,
  mode,
  seats,
  price,
  goToPreviousStep,
  submitting,
  creditLevel,
  promoCode,
  companies,
  creditPackages,
  confirmedPricing,
  skipReload = false,
  selectedServicePackages = [],
  connectedAccount,
  firstMonthSpecial,
}: {
  displayName: string;
  email: string;
  setModalOpen: Function;
  getUsage: Function;
  getToken: Function;
  eventListener: Function;
  productPlan: string;
  productPlanName?: string;
  hasConfiguration?: boolean;
  billingPeriod: string;
  mode: string;
  seats: number;
  price: number;
  goToPreviousStep: () => void;
  submitting: boolean;
  creditLevel: string;
  promoCode?: string;
  companies?: number;
  creditPackages?: number;
  confirmedPricing?: any;
  skipReload?: boolean;
  selectedServicePackages?: SelectedServicePackage[];
  connectedAccount?: string;
  firstMonthSpecial?: {
    applied: boolean;
    price: number;
    regularPrice: number;
    label: string;
  } | null;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    if (submitting && !modalLoading) {
      processPayment();
    }
  }, [submitting]);

  const processPayment = async () => {
    if (elements === null || stripe === null) {
      return;
    }

    try {
      setModalLoading(true);

      // First, submit the form to validate card details
      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      // Then, get current token to prepare for API call
      const currentToken = await getToken();

      // Calculate the expected total amount (for validation only)
      // Use confirmed pricing from backend if available, otherwise calculate from price
      const expectedAmount =
        confirmedPricing?.totalAmount ||
        (billingPeriod === "annual" ? price * 12 : price);
      console.log("Expected total amount:", expectedAmount);

      // Build return URL with necessary parameters for redirect handling
      const returnUrl = new URL(
        window.location.origin + window.location.pathname
      );
      returnUrl.searchParams.set("product_plan", productPlan);
      returnUrl.searchParams.set("billing_period", billingPeriod);
      returnUrl.searchParams.set("credit_level", creditLevel);
      returnUrl.searchParams.set("seats", seats.toString());
      if (companies !== undefined) {
        returnUrl.searchParams.set("companies", companies.toString());
      }
      if (creditPackages !== undefined) {
        returnUrl.searchParams.set(
          "credit_packages",
          creditPackages.toString()
        );
      }
      returnUrl.searchParams.set("payment_redirect", "true");

      // Confirm the payment with the client secret
      const { error: confirmError, paymentIntent } =
        await stripe.confirmPayment({
          elements,
          confirmParams: {
            return_url: returnUrl.toString(),
            payment_method_data: {
              billing_details: {
                name: displayName,
                email: email,
              },
            },
          },
          redirect: "if_required",
        });

      if (confirmError) {
        throw new Error(confirmError.message);
      }

      if (paymentIntent && paymentIntent.status === "succeeded") {
        console.log("Payment successful:", paymentIntent.id);

        // Create the subscription now that payment has succeeded
        // Build payload with conditional fields to ensure they're included
        const confirmPayload: any = {
          paymentIntent: paymentIntent,
          productPlan,
          billingPeriod,
          seats,
          creditLevel,
          domain: window.location.hostname,
        };

        // Only add optional fields if they're defined
        if (promoCode !== undefined && promoCode !== null) {
          confirmPayload.promoCode = promoCode;
        }
        if (companies !== undefined && companies !== null) {
          confirmPayload.companies = companies;
        }
        if (creditPackages !== undefined && creditPackages !== null) {
          confirmPayload.creditPackages = creditPackages;
        }

        // Include service packages in the confirm request (charged atomically)
        if (selectedServicePackages.length > 0 && paymentIntent.payment_method) {
          confirmPayload.servicePackageIds = selectedServicePackages.map(
            (pkg) => pkg.id
          );
          confirmPayload.paymentMethodId = paymentIntent.payment_method;
        }

        // Include connected account for direct charges (whitelabel)
        if (connectedAccount) {
          confirmPayload.connectedAccount = connectedAccount;
        }

        console.log("Sending confirmation payload:", confirmPayload);

        const confirmResponse = await axios.post(
          `${hostname}/subscriptions/confirm`,
          confirmPayload,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${currentToken}`,
            },
          }
        );

        console.log(
          "Subscription confirmed:",
          confirmResponse.data.subscriptionId
        );

        // Log service package results if any were charged
        if (confirmResponse.data.servicePackages) {
          console.log(
            "Service packages charged:",
            confirmResponse.data.servicePackages
          );
        }

        // Show success message
        eventListener({
          type: "SET_SUCCESS",
          message: "Payment successful! Your subscription has been activated.",
        });

        capture("Subscription Success", {
          productPlan: productPlan,
          productPlanName: productPlanName,
          billingPeriod: billingPeriod,
          creditLevel: creditLevel || "standard",
          seats: seats,
          companies: companies,
          creditPackages: creditPackages,
          totalAmount: expectedAmount,
          promoCode: promoCode,
        });

        // Set person properties to persist subscription info
        setPersonProperties({
          subscriptionPlan: productPlan,
          subscriptionPlanName: productPlanName,
          billingPeriod: billingPeriod,
          creditLevel: creditLevel || "standard",
          seats: seats,
          companies: companies,
          creditPackages: creditPackages,
          subscriptionAmount: expectedAmount,
          hasActiveSubscription: true,
        });

        // Refresh usage and close dialog
        await getUsage();
        setModalOpen(false);
        setModalLoading(false);

        // Track conversion and then refresh page (await ensures conversion is sent before reload)
        await trackPurchaseConversion(expectedAmount, paymentIntent.id, email);

        // Refresh the page to unlock all features (unless skipReload is true)
        if (!skipReload) {
          window.location.reload();
        }
      } else {
        console.log("Payment requires further action:", paymentIntent?.status);
        // In this case, Stripe will redirect the user
      }

      // @ts-ignore
      if (window && window.fpr) {
        // @ts-ignore
        window.fpr("referral", { email: email });
      }
    } catch (err) {
      setModalLoading(false);

      // Log detailed error information
      console.error("Subscription error details:", err);

      // Capture error for analytics
      capture("Subscription Error", {
        productPlan: productPlan,
        billingPeriod: billingPeriod,
        error:
          typeof err === "object"
            ? (err as any).message || "Unknown error"
            : String(err),
        creditLevel: creditLevel || "standard",
      });

      // Show friendly error message to user
      let errorMessage = parseError(err);
      if (errorMessage.includes("no attached payment source")) {
        errorMessage =
          "Unable to process payment. Please check your payment details and try again.";
      }

      eventListener({
        type: "SET_ERROR",
        error: errorMessage,
      });
    }
  };

  // Get formatted price strings using the unified pricing function
  const getTotalPrice = () => {
    // If first month special is applied, calculate the actual amount due today
    if (firstMonthSpecial?.applied) {
      if (billingPeriod === "annual") {
        // Annual: 11 months at regular price + first month special
        const annualTotal = (price * 11) + firstMonthSpecial.price;
        return `$${annualTotal.toLocaleString()}`;
      } else {
        // Monthly: just the first month special price
        return `$${firstMonthSpecial.price.toLocaleString()}`;
      }
    }
    if (confirmedPricing) {
      // Use the confirmed pricing from backend
      return `$${confirmedPricing.totalAmount.toLocaleString()}`;
    }
    // Fallback to calculation based on price prop
    // For plans with configuration, the price is already the total
    // For simple plans, multiply by billing period
    const totalAmount = billingPeriod === "annual" ? price * 12 : price;
    return `$${totalAmount.toLocaleString()}`;
  };

  const getMonthlyPrice = () => {
    if (confirmedPricing) {
      // Use the confirmed pricing from backend
      return `$${confirmedPricing.monthlyPrice.toLocaleString()}`;
    }
    // Fallback to calculation based on price prop
    // Price should already be the monthly total
    return `$${price.toLocaleString()}`;
  };

  // Helper to format service package billing label
  const getPackageBillingLabel = (pkg: SelectedServicePackage) => {
    if (pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION) {
      return pkg.billingInterval === SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY
        ? "/month"
        : "/year";
    }
    return "one-time";
  };

  // Separate packages by billing type for display
  const oneTimePackages = selectedServicePackages.filter(
    (pkg) => pkg.billingType !== SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION
  );
  const monthlyPackages = selectedServicePackages.filter(
    (pkg) =>
      pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION &&
      pkg.billingInterval === SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY
  );
  const annualPackages = selectedServicePackages.filter(
    (pkg) =>
      pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION &&
      pkg.billingInterval !== SERVICE_PACKAGE_BILLING_INTERVAL_MONTHLY
  );

  // Calculate totals by type
  const oneTimeTotal = oneTimePackages.reduce((sum, pkg) => sum + pkg.price, 0);
  const monthlyTotal = monthlyPackages.reduce((sum, pkg) => sum + pkg.price, 0);
  const annualTotal = annualPackages.reduce((sum, pkg) => sum + pkg.price, 0);

  return (
    <Container>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h6">Payment Information</Typography>
        <Button variant="text" onClick={goToPreviousStep}>
          Exit
        </Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            mb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 1,
          }}
        >
          Order Summary
        </Typography>

        {hasConfiguration ? (
          // Itemized receipt for plans with configuration options
          <Box>
            {/* Plan and Billing Period Info */}
            <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Plan
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {productPlanName || "Business"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Billing
                </Typography>
                <Typography variant="body1" fontWeight="500">
                  {billingPeriod.charAt(0).toUpperCase() +
                    billingPeriod.slice(1)}
                </Typography>
              </Box>
              {companies && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Companies
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {companies}
                  </Typography>
                </Box>
              )}
              {seats && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Seats
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {seats}
                  </Typography>
                </Box>
              )}
              {confirmedPricing?.creditLimit && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Credits
                  </Typography>
                  <Typography variant="body1" fontWeight="500">
                    {confirmedPricing.creditLimit.toLocaleString()}/month
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Itemized Line Items */}
            <Box sx={{ mb: 2 }}>
              {/* Base Plan - use breakdown from backend if available */}
              {confirmedPricing?.breakdown ? (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {confirmedPricing.breakdown.basePlan.description}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      ${confirmedPricing.breakdown.basePlan.price}
                    </Typography>
                  </Box>

                  {/* Additional items from breakdown */}
                  {confirmedPricing.breakdown.items?.map(
                    (item: any, index: number) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.secondary">
                          {item.description}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          ${item.total}
                        </Typography>
                      </Box>
                    )
                  )}
                </>
              ) : (
                <>
                  {/* Fallback - show configuration summary if breakdown not available */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      {productPlanName || productPlan} - Base Plan
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Included
                    </Typography>
                  </Box>

                  {/* Show configured options */}
                  {companies && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Companies: {companies}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    </Box>
                  )}

                  {seats && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Seats: {seats}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    </Box>
                  )}

                  {creditPackages && creditPackages > 0 && (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Credit Packages: {creditPackages}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    </Box>
                  )}
                </>
              )}

              {/* Promo Code Discount */}
              {promoCode && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mb: 1,
                  }}
                >
                  <Typography variant="body2" color="success.main">
                    Promo Code: {promoCode}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    Applied
                  </Typography>
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Totals */}
            <Box>
              {/* Subscription line */}
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="body1">
                  {billingPeriod === "annual" ? "Annual Subscription" : "Monthly Subscription"}
                </Typography>
                <Typography variant="body1">
                  {getTotalPrice()}
                </Typography>
              </Box>

              {/* Add-ons line items */}
              {selectedServicePackages.length > 0 && (
                <>
                  {selectedServicePackages.map((pkg) => (
                    <Box
                      key={pkg.id}
                      sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
                    >
                      <Typography variant="body1">
                        {pkg.name} ({getPackageBillingLabel(pkg)})
                      </Typography>
                      <Typography variant="body1">
                        ${pkg.price.toLocaleString()}
                        {pkg.billingType === SERVICE_PACKAGE_BILLING_TYPE_SUBSCRIPTION && (
                          <Typography component="span" variant="body2" color="text.secondary">
                            {getPackageBillingLabel(pkg)}
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                  ))}
                </>
              )}

              {/* Total Due Today - subscription + all add-ons (first charge) */}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Total Due Today
                  {firstMonthSpecial?.applied && (
                    <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 0.5 }}>
                      ({billingPeriod === "annual" ? "first year" : "first month"} special)
                    </Typography>
                  )}
                </Typography>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  ${(
                    (firstMonthSpecial?.applied
                      ? billingPeriod === "annual"
                        ? (price * 11) + firstMonthSpecial.price
                        : firstMonthSpecial.price
                      : billingPeriod === "annual" ? price * 12 : price) +
                    oneTimeTotal +
                    monthlyTotal +
                    annualTotal
                  ).toLocaleString()}
                </Typography>
              </Box>

              {/* Show recurring costs note if any subscription add-ons */}
              {(monthlyTotal > 0 || annualTotal > 0) && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {monthlyTotal > 0 && `Includes $${monthlyTotal.toLocaleString()}/month recurring`}
                  {monthlyTotal > 0 && annualTotal > 0 && " and "}
                  {annualTotal > 0 && `$${annualTotal.toLocaleString()}/year recurring`}
                </Typography>
              )}
            </Box>
          </Box>
        ) : (
          // Simple summary for entrepreneur plan
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
            <Box sx={{ flex: "1 1 160px", minWidth: 160 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Plan
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {productPlanName ||
                  productPlan.charAt(0).toUpperCase() + productPlan.slice(1)}
              </Typography>
            </Box>

            <Box sx={{ flex: "1 1 160px", minWidth: 160 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Billing Period
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {billingPeriod.charAt(0).toUpperCase() + billingPeriod.slice(1)}
              </Typography>
            </Box>

            <Box sx={{ flex: "1 1 160px", minWidth: 160 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Monthly Price
              </Typography>
              <Typography variant="body1" fontWeight="500">
                {getMonthlyPrice()}
              </Typography>
            </Box>

            {billingPeriod === "annual" && (
              <Box
                sx={{
                  flex: "1 1 160px",
                  minWidth: 160,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Due Today
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="700"
                  color="primary.main"
                >
                  {getTotalPrice()}
                </Typography>
              </Box>
            )}

            {promoCode && (
              <Box sx={{ flex: "1 1 160px", minWidth: 160 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Promo Code
                </Typography>
                <Typography
                  variant="body1"
                  fontWeight="500"
                  color="success.main"
                >
                  {promoCode} Applied
                </Typography>
              </Box>
            )}

            {/* Service Packages (Add-ons) for simple view */}
            {selectedServicePackages.length > 0 && (
              <Box sx={{ flex: "1 1 100%", mt: 2, pt: 2, borderTop: "1px solid", borderColor: "divider" }}>
                {/* One-time add-ons */}
                {oneTimePackages.length > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Add-ons (One-time)
                    </Typography>
                    {oneTimePackages.map((pkg) => (
                      <Box
                        key={pkg.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">{pkg.name}</Typography>
                        <Typography variant="body2" fontWeight="500">
                          ${pkg.price.toLocaleString()}
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}

                {/* Monthly recurring add-ons */}
                {monthlyPackages.length > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: oneTimePackages.length > 0 ? 2 : 0 }}>
                      Add-ons (Monthly)
                    </Typography>
                    {monthlyPackages.map((pkg) => (
                      <Box
                        key={pkg.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">{pkg.name}</Typography>
                        <Typography variant="body2" fontWeight="500">
                          ${pkg.price.toLocaleString()}/month
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}

                {/* Annual recurring add-ons */}
                {annualPackages.length > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: (oneTimePackages.length > 0 || monthlyPackages.length > 0) ? 2 : 0 }}>
                      Add-ons (Annual)
                    </Typography>
                    {annualPackages.map((pkg) => (
                      <Box
                        key={pkg.id}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="body2">{pkg.name}</Typography>
                        <Typography variant="body2" fontWeight="500">
                          ${pkg.price.toLocaleString()}/year
                        </Typography>
                      </Box>
                    ))}
                  </>
                )}

                {/* Total Due Today */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                    pt: 2,
                    borderTop: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    Total Due Today
                    {firstMonthSpecial?.applied && (
                      <Typography component="span" variant="caption" color="primary.main" sx={{ ml: 0.5 }}>
                        ({billingPeriod === "annual" ? "first year" : "first month"} special)
                      </Typography>
                    )}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold" color="primary.main">
                    ${(
                      (firstMonthSpecial?.applied
                        ? billingPeriod === "annual"
                          ? (price * 11) + firstMonthSpecial.price
                          : firstMonthSpecial.price
                        : billingPeriod === "annual" ? price * 12 : price) +
                      oneTimeTotal +
                      monthlyTotal +
                      annualTotal
                    ).toLocaleString()}
                  </Typography>
                </Box>

                {/* Show recurring costs note if any subscription add-ons */}
                {(monthlyTotal > 0 || annualTotal > 0) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {monthlyTotal > 0 && `Includes $${monthlyTotal.toLocaleString()}/month recurring`}
                    {monthlyTotal > 0 && annualTotal > 0 && " and "}
                    {annualTotal > 0 && `$${annualTotal.toLocaleString()}/year recurring`}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        )}
      </Paper>
      <Paper
        variant="outlined"
        sx={{ p: 3, mb: 3, width: "100%", borderRadius: 2 }}
      >
        <Typography
          variant="h6"
          gutterBottom
          sx={{
            mb: 2,
            borderBottom: "1px solid",
            borderColor: "divider",
            pb: 1,
          }}
        >
          Payment Method
        </Typography>
        <PaymentElement
          options={{
            layout: {
              type: "tabs",
              defaultCollapsed: false,
            },
            wallets: {
              applePay: "auto",
              googlePay: "auto",
            },
          }}
        />
      </Paper>
    </Container>
  );
};

export default PaymentStep;
